import React, { useState, useMemo, useCallback } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import useDriverAllApi from "../../Sevices/Driver Api/useDriverAllApi";

const ViewKycDetails = ({
  baseurl,
  token,
  selectedDriver,
  setSelectedDriver,
  setKycDrivers,
  activeTab,
  handleStepSubmit,
  previewImage,
  setPreviewImage,
  openKycDialogId,
  setOpenKycDialogId,
  AdminId
}) => {
  const [driverKycStatus, setLocalDriverKycStatus] = useState(null);
  const [loadingAction, setLoadingAction] = useState(null);

const {
  updateDriverKycStatus,
  fetchDriverKycDetails,
} = useDriverAllApi();

  const kycId = useMemo(() => {
    if (!selectedDriver) return null;
    const id = selectedDriver?.id || selectedDriver?.driverId;
    return id;
  }, [selectedDriver?.id, selectedDriver?.driverId]);

  const images = useMemo(() => {
    if (!selectedDriver) return [];
    
    const driverIdentifier = selectedDriver.id;
    
    return [
      {
        src: selectedDriver.profile_photo || 
             selectedDriver.profilePicture || 
             `${baseurl}/uploads/kyc/${driverIdentifier}_profile_photo.jpg`,
        label: "Profile Photo",
        field: "profile_photo",
      },
      {
        src: selectedDriver.aadhaar_front || 
             selectedDriver.aadharFrontPhoto || 
             `${baseurl}/uploads/kyc/${driverIdentifier}_aadhaar_front.jpg`,
        label: "Aadhaar Front",
        field: "aadhaar_front",
      },
      {
        src: selectedDriver.aadhaar_back || 
             selectedDriver.aadharBackPhoto || 
             `${baseurl}/uploads/kyc/${driverIdentifier}_aadhaar_back.jpg`,
        label: "Aadhaar Back",
        field: "aadhaar_back",
      },
      {
        src: selectedDriver.pan_front || 
             selectedDriver.panCardFrontPhoto || 
             `${baseurl}/uploads/kyc/${driverIdentifier}_pan_front.jpg`,
        label: "PAN Card Front",
        field: "pan_front",
      },
      {
        src: selectedDriver.driving_licence_img || 
             selectedDriver.drivingLicenseFrontPhoto || 
             `${baseurl}/uploads/kyc/${driverIdentifier}_dl_front.jpg`,
        label: "DL Image",
        field: "driving_licence_img",
      },
      {
        src: selectedDriver.vehicle_name_plate_img || 
             selectedDriver.licensePlatePhoto || 
             `${baseurl}/uploads/kyc/${driverIdentifier}_vehicle_plate.jpg`,
        label: "Number Plate",
        field: "vehicle_name_plate_img",
      },
    ];
  }, [selectedDriver, baseurl]);

  const statusInfo = useMemo(() => {
    const currentStatus = driverKycStatus?.status || selectedDriver?.status || "";
    
    return {
      currentStatus,
      isApproved: currentStatus.toLowerCase() === "approved",
      isRejected: currentStatus.toLowerCase() === "rejected",
    };
  }, [driverKycStatus?.status, selectedDriver?.status]);

  const onSubmitStep = useCallback(async (stepNumber, stepData) => {
    await handleStepSubmit(stepNumber, stepData);
  }, [handleStepSubmit]);

  const closeModel = useCallback(() => {
    setOpenKycDialogId(null);
    setSelectedDriver(null);
    setPreviewImage(null);
    setLocalDriverKycStatus(null);
  }, [setOpenKycDialogId, setSelectedDriver, setPreviewImage]);

  const handleImageClick = useCallback((imageSrc) => {
    setPreviewImage(`${imageSrc}?t=${Date.now()}`);
  }, [setPreviewImage]);

  const handlePreviewClose = useCallback(() => {
    setPreviewImage(null);
  }, [setPreviewImage]);

const handleKycStatusUpdate = async (kycId, status) => {
  setLoadingAction(status);

  try {
    const result = await updateDriverKycStatus({
      baseurl,
      token,
      kycId,
      status,
    });

    if (result && result.responseCode === "00") {
      // Update selected driver
      setSelectedDriver((prev) => ({
        ...prev,
        status,
      }));

      // Update driver list
      setKycDrivers((prevDrivers) =>
        prevDrivers.map((driver) =>
          driver.id === kycId.toString()
            ? { ...driver, status }
            : driver
        )
      );

      toast.success(`KYC ${status} successfully!`);

      // Refresh KYC list if needed
      if (activeTab === "Kyc Details") {
        setTimeout(async () => {
          await fetchDriverKycDetails({
            baseurl,
            token,
            adminId: AdminId,
          });
        }, 1500);
      }
    } else {
      throw new Error(result?.responseMessage || "Something went wrong");
    }
  } catch (error) {
    console.error("KYC update error:", error);
    toast.error(`Failed to ${status} KYC`);
  } finally {
    setLoadingAction(null);
  }
};

  return (
    <div className="p-4 border rounded-md shadow-sm bg-white space-y-4">
      {openKycDialogId && selectedDriver && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={closeModel}
        >
          <div
            className="bg-white rounded-lg shadow-md max-w-5xl w-full max-h-[90vh] overflow-y-auto p-6 relative mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">KYC Details</h2>
              <button
                onClick={closeModel}
                className="text-gray-600 hover:text-gray-800"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="p-4 bg-white rounded-2xl shadow-md border border-gray-200 mb-4">
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">
                    Driver Details
                  </h2>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Driver Code:
                      </span>{" "}
                      {selectedDriver.driver_code || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Name:</span>{" "}
                      {selectedDriver.name || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Email:</span>{" "}
                      {selectedDriver.email || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Phone:</span>{" "}
                      {selectedDriver.phone || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Aadhaar No:
                      </span>{" "}
                      {selectedDriver.aadhaar_no || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">PAN No:</span>{" "}
                      {selectedDriver.pan_no || "N/A"}
                    </p>
                  </div>
                </div>

                {/* Vehicle Details */}
                <div className="p-4 bg-white rounded-2xl shadow-md border border-gray-200 mb-4">
                  <h2 className="text-lg font-semibold text-gray-700 mb-4">
                    Vehicle Details
                  </h2>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Driving Licence:
                      </span>{" "}
                      {selectedDriver.driving_licence || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Transport Type:
                      </span>{" "}
                      {selectedDriver.transport_type_id || "N/A"} (
                      {selectedDriver.transport_description || "N/A"})
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">
                        Licence Plate:
                      </span>{" "}
                      {selectedDriver.licence_plate || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">City:</span>{" "}
                      {selectedDriver.city || "N/A"}
                    </p>
                    <p className="text-sm text-gray-600">
                      <span className="font-medium text-gray-800">Area:</span>{" "}
                      {selectedDriver.area || "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                {images.map((item, index) => (
                  <div key={index} className="relative text-center">
                    {item.src && item.src !== "" && item.src !== "No URL" ? (
                      <div className="relative group">
                        <img
                          src={item.src}
                          alt={item.label}
                          className="w-full h-48 object-cover rounded mb-2 cursor-pointer hover:scale-105 transition-transform"
                          onClick={() => handleImageClick(item.src)}
                          onError={(e) => {
                            e.currentTarget.src =
                              "https://via.placeholder.com/300x200?text=No+Image";
                          }}
                        />
                      </div>
                    ) : (
                      <div className="w-full h-48 bg-gray-100 rounded mb-2 flex flex-col items-center justify-center text-sm text-gray-600">
                        <p className="font-medium">No Image</p>
                      </div>
                    )}
                    <h5 className="font-medium text-gray-800 mt-1">
                      {item.label}
                    </h5>
                  </div>
                ))}
              </div>

              {/* Preview Modal */}
              {previewImage && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
                  onClick={handlePreviewClose}
                >
                  <div
                    className="relative bg-white p-2 rounded-lg shadow-xl max-w-4xl max-h-[75vh]"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <img
                      src={previewImage}
                      alt="Preview"
                      className="max-w-full max-h-[65vh] object-contain rounded"
                      onError={(e) => {
                        e.target.src =
                          "https://via.placeholder.com/600x500?text=No+Image";
                      }}
                    />
                    <button
                      onClick={handlePreviewClose}
                      className="absolute top-2 right-2 bg-white rounded-full p-1 hover:bg-gray-100"
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              )}

              <div className="flex justify-center gap-4">
                {!statusInfo.isApproved && (
                  <button
                    className="bg-green-500 text-white px-6 py-2 rounded hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleKycStatusUpdate(kycId, "Approve", selectedDriver)}
                    disabled={loadingAction === "Approve" || !kycId}
                  >
                    {loadingAction === "Approve" ? "Processing..." : "Approve"}
                  </button>
                )}

                {!statusInfo.isRejected && (
                  <button
                    className="bg-red-500 text-white px-6 py-2 rounded hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => handleKycStatusUpdate(kycId, "Reject", selectedDriver)}
                    disabled={loadingAction === "Reject" || !kycId}
                  >
                    {loadingAction === "Reject" ? "Processing..." : "Reject"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewKycDetails;